/**
 * Contrato de entrada del informe ejecutivo de Alfatex.
 * Las cifras proceden del dashboard: este módulo no realiza peticiones HTTP.
 * Los importes son números (EUR), netos de IVA, no cadenas ya formateadas.
 */

export function esImporte(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

export function formatearImporte(value) {
  if (!esImporte(value)) return 'No disponible';
  return `${new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value).replace(/\u00a0/g, ' ')} EUR`;
}

export function formatearPorcentaje(value) {
  if (!esImporte(value)) return 'No disponible';
  return `${new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value).replace(/\u00a0/g, ' ')} %`;
}

export function fechaLegible(isoDate) {
  if (typeof isoDate !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) {
    return 'No indicada';
  }
  const [year, month, day] = isoDate.split('-').map(Number);
  const d = new Date(year, month - 1, day);
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) {
    return 'No indicada';
  }
  return new Intl.DateTimeFormat('es-ES', { day: '2-digit', month: 'long', year: 'numeric' }).format(d);
}

export function evaluarInforme(datos) {
  const errores = [];
  const empresa = datos?.empresa ?? {};
  const periodos = Array.isArray(datos?.periodos) ? datos.periodos : [];
  const pedidos = datos?.pedidos ?? {};
  const p2025 = periodos.find(p => Number(p?.anio) === 2025);
  const p2026 = periodos.find(p => Number(p?.anio) === 2026);

  if (!empresa.nombre || typeof empresa.nombre !== 'string') errores.push('Nombre de empresa');
  if (fechaLegible(empresa.fechaCorte) === 'No indicada') errores.push('Fecha de corte de las cifras');
  if (!p2025) errores.push('Ejercicio 2025');
  if (!p2026) errores.push('Ejercicio 2026');
  if (p2025 && !esImporte(p2025.resultadoConjunto)) errores.push('Resultado conjunto 2025');
  if (p2026 && !esImporte(p2026.resultadoConjunto)) errores.push('Resultado conjunto 2026');
  if (!esImporte(pedidos.importeFirmado)) errores.push('Importe de pedidos firmados');
  if (!esImporte(pedidos.costeProduccionPrevisto)) errores.push('Coste previsto de producción');
  if (!esImporte(pedidos.necesidadPicoTesoreria)) errores.push('Necesidad máxima de tesorería (calendario de cobros y pagos)');
  if (!esImporte(pedidos.financiacionSolicitada)) errores.push('Financiación solicitada');
  for (const periodo of [p2025, p2026].filter(Boolean)) {
    for (const [key, label] of Object.entries({
      facturacionTotal: 'Facturación', resultadoEnnage: 'Resultado contable Ennage',
      resultadoGenaen: 'Resultado de gestión Genaen', ebitda: 'EBITDA',
      fondoManiobra: 'Fondo de maniobra', deudaFinanciera: 'Deuda financiera',
    })) {
      if (!esImporte(periodo[key])) errores.push(`${label} ${periodo.anio}`);
    }
  }
  if (!esImporte(pedidos.anticiposClientes)) errores.push('Anticipos de clientes');
  if (!esImporte(pedidos.plazoMeses) || pedidos.plazoMeses <= 0) errores.push('Plazo previsto de financiación');

  const positivos = p2025 && p2026 &&
    esImporte(p2025.resultadoConjunto) && esImporte(p2026.resultadoConjunto) &&
    p2025.resultadoConjunto > 0 && p2026.resultadoConjunto > 0;

  const margen = esImporte(pedidos.margenBrutoPrevisto)
    ? pedidos.margenBrutoPrevisto
    : esImporte(pedidos.importeFirmado) && esImporte(pedidos.costeProduccionPrevisto)
      ? pedidos.importeFirmado - pedidos.costeProduccionPrevisto
      : null;
  const margenPct = esImporte(margen) && esImporte(pedidos.importeFirmado) && pedidos.importeFirmado !== 0
    ? margen / pedidos.importeFirmado * 100
    : null;

  return {
    empresa, pedidos, p2025, p2026,
    margen, margenPct,
    positivos: Boolean(positivos),
    borrador: errores.length > 0,
    errores,
  };
}
